import { RefObject } from 'react';

import {
    ActivatorElementType,
    Placement,
    VerticalDirection,
    HorizontalDirection,
    AlignmentForVerticalDirection,
    AlignmentForHorizontalDirection,
    ArrowGuardDistance,
    ParsedPlacement,
} from '@hh.ru/magritte-internal-drop-base/types';

interface GetMetricsFunc {
    (args: {
        activatorRef: RefObject<ActivatorElementType>;
        customViewport?: RefObject<HTMLElement>;
        dropRef: RefObject<HTMLElement>;
    }): {
        activatorRect: DOMRect;
        viewportRect: DOMRect;
        offsetParentRect: DOMRect;
    };
}

export const getMetrics: GetMetricsFunc = ({ activatorRef, customViewport, dropRef }) => {
    let activatorRect: DOMRect = new DOMRect();

    if (activatorRef.current) {
        activatorRect = activatorRef.current.getBoundingClientRect();
    }
    const viewportRect =
        customViewport && customViewport.current
            ? customViewport.current.getBoundingClientRect()
            : DOMRect.fromRect({
                  width: window.visualViewport?.width,
                  height: window.visualViewport?.height,
                  x: 0,
                  y: 0,
              });
    const offsetParentRect = dropRef.current?.offsetParent?.getBoundingClientRect() || new DOMRect();

    return { activatorRect, viewportRect, offsetParentRect };
};

const parsePlacement = (
    placement: Placement
): [VerticalDirection | HorizontalDirection, AlignmentForHorizontalDirection | AlignmentForVerticalDirection] => {
    return placement.split('-') as [
        VerticalDirection | HorizontalDirection,
        AlignmentForHorizontalDirection | AlignmentForVerticalDirection,
    ];
};

type Rect = { left: number; top: number; width: number; height: number; right: number; bottom: number };
// Возвращает описание прямоугольника и размеры и позицию правой и нижней стороны, при этом позволяет не указывать
// в аргументах либо размеры, либо позицию правой и нижней стороны
const createRect = (args: {
    left: number;
    top: number;
    width?: number;
    height?: number;
    right?: number;
    bottom?: number;
}) => ({
    left: args.left,
    top: args.top,
    width: args.width || (args?.right || 0) - args.left,
    height: args.height || (args?.bottom || 0) - args.top,
    right: args.right || (args?.width || 0) + args.left,
    bottom: args.bottom || (args?.height || 0) + args.top,
});

interface GetAvailableRectsForPlacementsFunc {
    (args: { placements: Placement[]; activatorRect: DOMRect; viewportRect: DOMRect }): { [key in Placement]: Rect };
}

const getAvailableRectsForPlacements: GetAvailableRectsForPlacementsFunc = ({
    placements,
    activatorRect,
    viewportRect,
}) => {
    // Вычисляем содержащий прямоугольник для свободного пространство с каждой из сторон от активатора
    // https://excalidraw.com/#json=3O9770_G2g6lkJuIftLiQ,2ZRIYkXrSjCpobPS5Ufh7w
    const freeSpaceBySide = {
        top: () =>
            createRect({
                left: viewportRect.x,
                top: viewportRect.y,
                width: viewportRect.width,
                height: activatorRect.top - viewportRect.top,
            }),
        right: () =>
            createRect({
                left: activatorRect.right,
                top: viewportRect.top,
                width: viewportRect.right - activatorRect.right,
                height: viewportRect.height,
            }),
        bottom: () =>
            createRect({
                left: viewportRect.x,
                top: activatorRect.bottom,
                width: viewportRect.width,
                height: viewportRect.bottom - activatorRect.bottom,
            }),
        left: () =>
            createRect({
                left: viewportRect.x,
                top: viewportRect.y,
                width: activatorRect.left - viewportRect.left,
                height: viewportRect.height,
            }),
    };

    // Для каждого варианта позиционирования вычисляем содержащий прямоугольник доступного пространства
    // с учетом позиции активатора и границ вьюпорта
    return placements.reduce(
        (acc, placement) => {
            const [direction, alignment] = parsePlacement(placement);

            const freeSpace = freeSpaceBySide[direction]();
            acc[placement] = freeSpace;

            // Берем свободное пространство с той стороны от активатора куда должен выпасть дроп
            // и устанавливает позицию стороны соответствующей alignment равно позиции той же стороны активатора.
            // На схеме понятнее откуда берется эта логика:
            // https://excalidraw.com/#json=cTKcB__3Oi3U3lF1Z4FLk,6jWpaYZLXePae-FDUbC9PQ
            if (alignment !== 'center') {
                acc[placement][alignment] = activatorRect[alignment];
                acc[placement] = createRect({ ...acc[placement], width: undefined, height: undefined });
            }
            return acc;
        },
        {} as { [key in Placement]: Rect }
    );
};

const vertPlacement = ['top', 'bottom'];
const axisStartAlignment = ['left', 'top'];

export interface FindBestFitPlacementFunc {
    (args: {
        /** Список доступных направлений для выпадения, напр.: `['top-left', 'left-bottom']` */
        placements: Placement[];
        /** Содержащий прямоугольник активатора */
        activatorRect: DOMRect;
        /** Содержащий прямоугольник viewport */
        viewportRect: DOMRect;
        /** Содержащий прямоугольник offsetParent дропа */
        offsetParentRect: DOMRect;
        /** Ширина дропа */
        dropWidth: number;
        /** Высота дропа */
        dropHeight: number;
        /** Расстояние от дропа до активатора */
        guardDistanceToActivator: number;
        /** Расстояние от дропа до верхней и нижней границы экрана */
        guardDistanceToViewport: number;
        /** Расстояние от центра стрелки (например в тултипе), до ближайшего края дропа */
        arrowGuardDistance: ArrowGuardDistance;
        /**
         * Минимальная высота дропа, используется для вычисления позиции и размера дропа в случае когда
         * необходимо уместить дроп во `viewport`. Если дроп не умещается во `viewport`, то его высота будет уменьшаться
         * до достижения этого значения. После достижения будет игнорироваться параметр `guardDistanceToViewport`.
         * */
        minDropHeight: number;
        /** Флаг, указывающий что требуется пытаться уместить дроп во viewport за счет изменения его размеров */
        allowShrinkHeightToFitIntoViewport: boolean;
        /** Флаг, указывающий что ширина дропа должна быть равна ширине активатора */
        widthEqualToActivator: boolean;
        /** Позволяет временно отключить изменение расположения, например при открытии экранной клавиатуры */
        lockPlacement?: boolean;
        prefferedPlacement: Placement | undefined;
    }): {
        /** Содержащий прямоугольник выбранного места размещения дропа. Задает позицию и размер дропа  */
        rect: DOMRect;
        /**
         * Вариант из списка переданного в параметре `placements` выбранный в качестве
         * наиболее подходящего для размещения дропа
         * */
        parsedPlacement: ParsedPlacement;
        placement: Placement;
    };
}

/**
 * Функция находит наилучшее расположение для дропа с учетом доступного пространства.
 * Наилучшее расположение выбирается из списка допустимых (`placements`).
 * Алгоритм выбора следующий:
 * - Если дроп полностью умещается на экране при выпадении в соответствующем направлении, то выбирается это направление.
 * - Если таких направлений несколько, то выбирается направление с наименьшим индексом в списке.
 * - Если ни одно из направлений не может полностью уместить дроп, то выбирается направление, которое умещает наибольшую
 * часть дропа по вертикали. Если таких направлений несколько, то среди них выбирается первое направление полностью
 * умещающее дроп по ширине, или умещающее наибольшую часть его ширины.
 */
export const findBestFitPlacement: FindBestFitPlacementFunc = ({
    placements,
    activatorRect,
    viewportRect,
    offsetParentRect,
    dropWidth,
    dropHeight,
    guardDistanceToActivator,
    guardDistanceToViewport,
    minDropHeight,
    allowShrinkHeightToFitIntoViewport,
    widthEqualToActivator,
    arrowGuardDistance,
    prefferedPlacement,
    lockPlacement,
}) => {
    const availablePlacements = getAvailableRectsForPlacements({ placements, activatorRect, viewportRect });
    const actualWidth = widthEqualToActivator ? activatorRect.width : dropWidth;

    // Прямоугольники описывающие место необходимое для размещения дропа при вертикальном и горизонтальном размещении.
    // С учетом отступов от активатора и от краев экрана.
    const dropRectForVertPlacement = DOMRect.fromRect({
        x: 0,
        y: 0,
        width: actualWidth,
        height:
            dropHeight + (allowShrinkHeightToFitIntoViewport ? guardDistanceToViewport : 0) + guardDistanceToActivator,
    });

    const dropRectForHorPlacement = DOMRect.fromRect({
        x: 0,
        y: 0,
        width: actualWidth + guardDistanceToActivator,
        height: dropHeight + (allowShrinkHeightToFitIntoViewport ? guardDistanceToViewport : 0),
    });
    const viewport = createRect(viewportRect);
    const activator = createRect(activatorRect);

    // хранит выбранный лучший вариант размещения, может меняться в процессе перебора вариантов
    let bestFitPlacement = prefferedPlacement || placements[0];
    let bestFitPlacementDistanceToViewportBorder = Number.MIN_SAFE_INTEGER;
    let bestFitPlacementArrowGuardOffset = 0;
    // Минимальная высота необходимая для размещения дропа
    const minHeightToFit = allowShrinkHeightToFitIntoViewport ? minDropHeight + guardDistanceToViewport : dropHeight;

    const iterationsCount = prefferedPlacement ? placements.length + 1 : placements.length;
    for (let i = 0, j = lockPlacement && prefferedPlacement ? 1 : iterationsCount; i < j; i++) {
        // Если передан prefferedPlacement, то сначала надо проверить его
        // При этом если он не подошел, то при дальнейшей обработке массива допустимых размещений
        // снова наткнемся на него, в этом случае нужно сразу перейти к следующему элементу
        let placement = placements[i];
        if (prefferedPlacement) {
            if (i === 0) {
                placement = prefferedPlacement;
            } else {
                placement = placements[i - 1];
                if (placement === prefferedPlacement) {
                    // eslint-disable-next-line no-continue
                    continue;
                }
            }
        }
        const freeSpace = availablePlacements[placement];
        const [direction, alignment] = parsePlacement(placement);
        const isVertPlacement = vertPlacement.includes(direction);
        const dropRect = isVertPlacement ? dropRectForVertPlacement : dropRectForHorPlacement;

        /*
         * Если есть стрелка и для нее задан сдвиг, то стрелка должна лежать со стороны от центра активатора равной
         * alignment. Т.е. чтобы соблюсти это правило нужно проверить что сдвиг стрелки меньше или равен половине ширины
         * или высоты активатора в зависимости от направления размещения дропа. Если это не так, то дроп необходимо
         * сдвинуть так, чтобы стрелка была по середине активатора. Это может повлиять на величину пересечения дропа
         * с границей вьюпорта
         */
        const arrowGuardDistanceForPlacement =
            arrowGuardDistance[direction]?.[
                alignment as AlignmentForVerticalDirection & AlignmentForHorizontalDirection
            ];
        let arrowGuardOffset = 0;
        if (
            arrowGuardDistanceForPlacement &&
            arrowGuardDistanceForPlacement > activatorRect[isVertPlacement ? 'width' : 'height'] / 2
        ) {
            arrowGuardOffset = arrowGuardDistanceForPlacement - activatorRect[isVertPlacement ? 'width' : 'height'] / 2;
        }

        // Расчитываем расстояние от стороны по которой выравнен дроп до границы вьюпорта
        // Нужно для обработки случаев когда часть активатора уходит за границы экрана, например вниз.
        // В этом случае от нижнего края активатора до границы вьюпорта места больше чем от верхнего края активатора
        // до границы вьюпорта. Но спозиционировать дроп оптимальнее по верхней границе активатора, т.к. в этом случае
        // меньшая часть дропа будет уходить за границы экрана.
        // https://excalidraw.com/#json=V-b0E4ic-4RYECCPfjQ56,gMxZkd6HDGw9kIBksRBylQ
        let distanceToViewportBorder = 0;
        if (alignment !== 'center') {
            distanceToViewportBorder = axisStartAlignment.includes(alignment)
                ? freeSpace[alignment] - viewport[alignment]
                : viewport[alignment] - freeSpace[alignment];
            distanceToViewportBorder -= arrowGuardOffset;
        } else {
            const dropRect = createRect({
                // Т.к. к варианту с alignment=center не применяется изменение размера дропа, то для вычисления
                // пересечения дропа с границами экрана в этом варианте размещения можно использовать прямоугольник
                // размеры которого равны размерам дропа, центр которого расположен в центре активатора.
                left: activator.left - (actualWidth - activator.width) / 2,
                top: activator.top - (actualWidth - activator.height) / 2,
                width: actualWidth,
                height: dropHeight,
            });
            // С разных сторон может быть разная величина пересечения, берем худший вариант.
            distanceToViewportBorder = isVertPlacement
                ? Math.min(dropRect.left - viewport.left, viewport.right - dropRect.right)
                : Math.min(dropRect.top - viewport.top, viewport.bottom - dropRect.bottom);
        }

        if (freeSpace.width >= dropRect.width && freeSpace.height >= dropRect.height && distanceToViewportBorder >= 0) {
            bestFitPlacement = placement;
            bestFitPlacementArrowGuardOffset = arrowGuardOffset;
            break;
        }

        const currentCanFitByHeight =
            freeSpace.height >= minHeightToFit + (isVertPlacement ? guardDistanceToActivator : 0);
        const currentNotWorseByHeight =
            freeSpace.height >= availablePlacements[bestFitPlacement].height || freeSpace.height >= dropRect.height;
        const currentFitsBetterByHeight =
            availablePlacements[bestFitPlacement].height < dropHeight &&
            freeSpace.height > availablePlacements[bestFitPlacement].height;

        const currentNotWorseByWidth =
            freeSpace.width >= availablePlacements[bestFitPlacement].width || freeSpace.width >= dropRect.width;
        const currentFitsBetterByWidth =
            availablePlacements[bestFitPlacement].width < dropRect.width &&
            freeSpace.width > availablePlacements[bestFitPlacement].width;

        const currentFitsBetter =
            (currentFitsBetterByHeight && currentNotWorseByWidth && distanceToViewportBorder >= 0) ||
            (currentFitsBetterByWidth && currentNotWorseByHeight && distanceToViewportBorder >= 0) ||
            (currentCanFitByHeight &&
                currentNotWorseByWidth &&
                bestFitPlacementDistanceToViewportBorder < 0 &&
                distanceToViewportBorder > bestFitPlacementDistanceToViewportBorder);

        if (currentFitsBetter) {
            bestFitPlacement = placement;
            bestFitPlacementDistanceToViewportBorder = distanceToViewportBorder;
            bestFitPlacementArrowGuardOffset = arrowGuardOffset;
        }
    }

    // После того как найден содержащий прямоугольник для дропа вычисляем высоту дропа, затем позицию
    const [direction, alignment] = parsePlacement(bestFitPlacement);
    const freeSpace = availablePlacements[bestFitPlacement];
    const isVertPlacement = vertPlacement.includes(direction);
    const actualHeight =
        allowShrinkHeightToFitIntoViewport && alignment !== 'center'
            ? Math.min(
                  dropHeight,
                  Math.max(
                      minDropHeight,
                      freeSpace.height - guardDistanceToViewport - (isVertPlacement ? guardDistanceToActivator : 0)
                  )
              )
            : dropHeight;

    // Для определения позиции дропа необходимо вычислить left/top, но для некоторых вариантов размещения проще
    // вычислить right/bottom т.к. они совпадают с соответствующими сторонами содержащего прямоугольника.
    // Поэтому сначала проверяем можем ли мы вычислить right/bottom.
    const position: Partial<Rect> = {};
    if (direction === 'left' || alignment === 'right') {
        position.right = freeSpace.right;
    }
    if (direction === 'top' || alignment === 'bottom') {
        position.bottom = freeSpace.bottom;
    }
    // Вычисляем left/top на основе right/bottom и width/height если они есть. Если их нет - значит в этом варианте
    // размещения с содержащим прямоугольником совпадают left/top.
    position.left = position.right !== undefined ? position.right - actualWidth : freeSpace.left;
    position.top = position.bottom !== undefined ? position.bottom - actualHeight : freeSpace.top;

    // Отдельно вычисляем позицию/размер для выравнивания по центру
    if (alignment === 'center') {
        const startPosAttr = isVertPlacement ? 'left' : 'top';
        const sizeAttr = isVertPlacement ? 'width' : 'height';
        const dropSize = isVertPlacement ? actualWidth : actualHeight;
        position[startPosAttr] = activatorRect[startPosAttr] + (activatorRect[sizeAttr] - dropSize) / 2;
    }

    // Отодвигаем на заданный отступ от активатора
    const signedActivatorOffset = ['bottom', 'right'].includes(direction)
        ? guardDistanceToActivator
        : -guardDistanceToActivator;
    position.top += isVertPlacement ? signedActivatorOffset : 0;
    position.left += !isVertPlacement ? signedActivatorOffset : 0;

    position.width = actualWidth;
    position.height = actualHeight;
    // Создаем новую переменную чтобы поменять тип, потому что на этом этапе мы точно знаем что у нас есть эти поля
    // и нет необходимости писать тайпгарды в каждом месте использования
    const finalPosition = position as { left: number; top: number; width: number; height: number };

    // Если необходим сдвиг для выравнивания стрелки относительно активатора - применяем его
    if (bestFitPlacementArrowGuardOffset) {
        finalPosition[isVertPlacement ? 'left' : 'top'] += axisStartAlignment.includes(alignment)
            ? -bestFitPlacementArrowGuardOffset
            : bestFitPlacementArrowGuardOffset;
        // Добавляем дополнительно пространство к размеру дропа
        finalPosition[isVertPlacement ? 'height' : 'width'] += bestFitPlacementArrowGuardOffset;
        // Удостоверяемся что после этого размер дропа не превышает размер необходимый для его контента
        finalPosition.height = Math.min(finalPosition.height, dropHeight);
        finalPosition.width = Math.min(finalPosition.width, actualWidth);
    }

    return {
        rect: new DOMRect(
            finalPosition.left - offsetParentRect.left, // Переводим координаты в систему координат offsetParent
            finalPosition.top - offsetParentRect.top,
            finalPosition.width,
            finalPosition.height
        ),
        parsedPlacement: { direction, alignment },
        placement: bestFitPlacement,
    };
};

export const applyPlacementToDrop = (dropRef: RefObject<HTMLElement>, placement: DOMRect): void => {
    if (!dropRef.current) {
        return;
    }

    dropRef.current.style.top = `${placement.top}px`;
    dropRef.current.style.left = `${placement.left}px`;
    dropRef.current.style.width = `${placement.width}px`;
    dropRef.current.style.height = `${placement.height}px`;
};
